import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Alert, Button, Badge, Spinner } from 'react-bootstrap';
import SkeletonLoader, { EventHeaderSkeleton, EventContentSkeleton } from '../components/common/SkeletonLoader';
import { apiService } from '../services/api';
import SEO from '../components/common/SEO';
import './AddonDetailPage.scss';

interface AddonVariant {
  id: number;
  name: string;
  description: string;
  price: number;
  stock: number;
  in_stock: boolean;
  image?: string;
  image_url?: string;
}

interface AddonCluster {
  id: number;
  name: string;
  description?: string;
}

interface Addon {
  id: number;
  name: string;
  description: string;
  price: number;
  price_display: string;
  image?: string;
  image_url?: string;
  stock: number;
  in_stock: boolean;
  has_variants: boolean;
  min_price: number;
  max_price: number;
  addon_type: string;
  hidden: boolean;
  variants: AddonVariant[];
  cluster?: AddonCluster;
  currency?: {
    id: number;
    code: string;
    symbol: string;
  };
  event?: {
    id: number;
    title: string;
    slug: string;
  };
}

const AddonDetailPage: React.FC = () => {
  const { addonId } = useParams<{ addonId: string }>();
  const navigate = useNavigate();
  const [addon, setAddon] = useState<Addon | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<AddonVariant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    const fetchAddonDetail = async () => {
      if (!addonId) {
        setError('Addon ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await apiService.addon.getAddonDetail(parseInt(addonId));
        setAddon(data);
        
        // If addon has variants, select the first available one
        if (data.has_variants && data.variants && data.variants.length > 0) {
          const firstInStock = data.variants.find((v: AddonVariant) => v.in_stock) || data.variants[0];
          setSelectedVariant(firstInStock);
        }
      } catch (err: any) {
        console.error('Error fetching addon detail:', err);
        setError(err.response?.data?.error || 'Failed to load addon details');
      } finally {
        setLoading(false);
      }
    };

    fetchAddonDetail();
  }, [addonId]);

  const handleAddToCart = async () => {
    if (!addon) return;

    // Validate selection
    if (addon.has_variants && !selectedVariant) {
      alert('Please select a variant');
      return;
    }

    if (quantity < 1) {
      alert('Quantity must be at least 1');
      return;
    }

    // Check stock
    if (addon.has_variants && selectedVariant) {
      if (selectedVariant.stock < quantity) {
        alert(`Only ${selectedVariant.stock} items available in stock`);
        return;
      }
    } else {
      if (addon.stock < quantity) {
        alert(`Only ${addon.stock} items available in stock`);
        return;
      }
    }

    try {
      setAddingToCart(true);
      
      // Prepare information object for variant if applicable
      const information: any = {};
      if (addon.has_variants && selectedVariant) {
        information.variant_id = selectedVariant.id.toString();
        information.variant_name = selectedVariant.name;
        information.variant_price = selectedVariant.price.toString();
      }
      
      // Use cart service to add addon with variant information
      await apiService.cart.addToCart('addon', addon.id, quantity, Object.keys(information).length > 0 ? information : undefined);
      
      // Dispatch cart update event
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      
      // Navigate to cart or show success message
      if (window.confirm('Addon added to cart! Would you like to view your cart?')) {
        navigate('/cart');
      }
    } catch (err: any) {
      console.error('Error adding to cart:', err);
      alert(err.response?.data?.error || err.response?.data?.message || 'Failed to add addon to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const formatPrice = (price: number) => {
    const symbol = addon?.currency?.symbol || '৳';
    return `${symbol}${price.toFixed(2)}`;
  };

  const getMaxQuantity = () => {
    if (addon?.has_variants && selectedVariant) {
      return selectedVariant.stock;
    }
    return addon?.stock || 0;
  };

  if (loading) {
    return (
      <div className="addon-detail-page">
        <EventHeaderSkeleton />
        <Container className="container-xl mt-4">
          <Row>
            <Col md={7}>
              <EventContentSkeleton />
            </Col>
            <Col md={5}>
              <div className="card p-4" style={{marginTop: "2rem"}}>
                <SkeletonLoader type="text" width="70%" height="24px" className="mb-3" />
                <SkeletonLoader type="text" width="100%" height="20px" className="mb-2" />
                <SkeletonLoader type="text" width="80%" height="20px" className="mb-4" />
                <SkeletonLoader type="rect" width="100%" height="48px" />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (error || !addon) {
    return (
      <div className="addon-detail-page" style={{ marginTop: '120px', minHeight: '400px' }}>
        <Container>
          <Alert variant="danger" className="mt-4">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error || 'Addon not found'}</p>
            <Link to="/events" className="btn btn-primary">
              Browse Events
            </Link>
          </Alert>
        </Container>
      </div>
    );
  }

  // Generate SEO data for addon
  const addonTitle = `${addon.name} | Quicket`;
  const addonDescription = addon.description 
    ? addon.description.replace(/<[^>]*>/g, '').substring(0, 160)
    : `Purchase ${addon.name} on Quicket`;
  const addonImage = addon.image_url || addon.image;
  const addonUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="addon-detail-page" style={{ marginTop: '120px' }}>
      <SEO
        title={addonTitle}
        description={addonDescription}
        image={addonImage}
        type="article"
        url={addonUrl}
      />
      <Container className="container-xl">
        {/* Breadcrumb */}
        <nav aria-label="breadcrumb" className="addon-breadcrumb mb-4">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">
                <i className="fas fa-home me-1"></i>Home
              </Link>
            </li>
            {addon.event && (
              <>
                <li className="breadcrumb-item">
                  <Link to="/events">Events</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={`/events/${addon.event.slug}`}>{addon.event.title}</Link>
                </li>
              </>
            )}
            <li className="breadcrumb-item active" aria-current="page">
              {addon.name}
            </li>
          </ol>
        </nav>

        <Row className="g-4">
          {/* Left Column - Image */}
          <Col lg={6} className="mb-4 mb-lg-0">
            <div className="addon-image-wrapper">
              <div className="addon-image-container">
                {addon.image_url || addon.image ? (
                  <div className="image-zoom-container">
                    <img
                      src={addon.image_url || addon.image}
                      alt={addon.name}
                      className="addon-main-image"
                    />
                    <div className="image-overlay">
                      <i className="fas fa-search-plus"></i>
                    </div>
                  </div>
                ) : (
                  <div className="addon-image-placeholder">
                    <div className="placeholder-icon">
                      <i className="fas fa-image"></i>
                    </div>
                    <p>No image available</p>
                  </div>
                )}
              </div>
              {/* Image Badge Overlay */}
              {addon.cluster && (
                <div className="image-badge">
                  <i className="fas fa-tag me-1"></i>
                  {addon.cluster.name}
                </div>
              )}
            </div>
          </Col>

          {/* Right Column - Details */}
          <Col lg={6}>
            <div className="addon-details-card">
              {/* Header Section */}
              <div className="addon-header">
                {addon.cluster && (
                  <Badge bg="primary" className="cluster-badge mb-3">
                    <i className="fas fa-layer-group me-1"></i>
                    {addon.cluster.name}
                  </Badge>
                )}

                <h1 className="addon-title">{addon.name}</h1>
                
                {addon.cluster?.description && (
                  <p className="cluster-description">{addon.cluster.description}</p>
                )}
              </div>

              {/* Price Section */}
              <div className="addon-price-section">
                {addon.has_variants ? (
                  <div className="price-container">
                    {selectedVariant ? (
                      <>
                        <div className="price-label">Price</div>
                        <div className="addon-price-large">{formatPrice(selectedVariant.price)}</div>
                      </>
                    ) : (
                      <>
                        <div className="price-label">Starting from</div>
                        {addon.min_price === addon.max_price ? (
                          <div className="addon-price-large">{formatPrice(addon.min_price)}</div>
                        ) : (
                          <div className="addon-price-large">
                            {formatPrice(addon.min_price)} - {formatPrice(addon.max_price)}
                          </div>
                        )}
                        <small className="price-hint">Select a variant to see exact price</small>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="price-container">
                    <div className="price-label">Price</div>
                    <div className="addon-price-large">{formatPrice(addon.price)}</div>
                  </div>
                )}
              </div>

              {/* Stock Status */}
              <div className="addon-stock-status">
                {addon.has_variants ? (
                  selectedVariant ? (
                    <div className={`stock-badge ${selectedVariant.in_stock ? 'in-stock' : 'out-of-stock'}`}>
                      <i className={`fas ${selectedVariant.in_stock ? 'fa-check-circle' : 'fa-times-circle'} me-2`}></i>
                      <span>
                        {selectedVariant.in_stock 
                          ? `In Stock (${selectedVariant.stock} available)` 
                          : 'Out of Stock'}
                      </span>
                    </div>
                  ) : (
                    <div className="stock-badge info">
                      <i className="fas fa-info-circle me-2"></i>
                      <span>Select a variant to check availability</span>
                    </div>
                  )
                ) : (
                  <div className={`stock-badge ${addon.in_stock ? 'in-stock' : 'out-of-stock'}`}>
                    <i className={`fas ${addon.in_stock ? 'fa-check-circle' : 'fa-times-circle'} me-2`}></i>
                    <span>
                      {addon.in_stock 
                        ? `In Stock (${addon.stock} available)` 
                        : 'Out of Stock'}
                    </span>
                  </div>
                )}
              </div>

              {/* Variants Selection */}
              {addon.has_variants && addon.variants && addon.variants.length > 0 && (
                <div className="addon-variants">
                  <div className="section-header">
                    <h5 className="section-title">
                      <i className="fas fa-palette me-2"></i>
                      Select Variant
                    </h5>
                    <span className="section-subtitle">Choose your preferred option</span>
                  </div>
                  <div className="variants-grid">
                    {addon.variants.map((variant) => (
                      <div
                        key={variant.id}
                        className={`variant-card ${selectedVariant?.id === variant.id ? 'selected' : ''} ${!variant.in_stock ? 'out-of-stock' : ''}`}
                        onClick={() => variant.in_stock && setSelectedVariant(variant)}
                      >
                        <div className="variant-image-wrapper">
                          {variant.image_url || variant.image ? (
                            <img
                              src={variant.image_url || variant.image}
                              alt={variant.name}
                              className="variant-image"
                            />
                          ) : (
                            <div className="variant-image-placeholder">
                              <i className="fas fa-image"></i>
                            </div>
                          )}
                          {selectedVariant?.id === variant.id && (
                            <div className="selected-indicator">
                              <i className="fas fa-check-circle"></i>
                            </div>
                          )}
                          {!variant.in_stock && (
                            <div className="out-of-stock-overlay">
                              <span>Out of Stock</span>
                            </div>
                          )}
                        </div>
                        <div className="variant-info">
                          <div className="variant-name">{variant.name}</div>
                          <div className="variant-price">{formatPrice(variant.price)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity and Actions Section */}
              <div className="addon-actions-section">
                {((addon.has_variants && selectedVariant?.in_stock) || (!addon.has_variants && addon.in_stock)) && (
                  <div className="addon-quantity">
                    <label htmlFor="quantity" className="quantity-label">
                      <i className="fas fa-sort-numeric-up me-2"></i>
                      Quantity
                    </label>
                    <div className="quantity-selector">
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={quantity <= 1}
                        aria-label="Decrease quantity"
                      >
                        <i className="fas fa-minus"></i>
                      </button>
                      <input
                        type="number"
                        id="quantity"
                        className="quantity-input"
                        value={quantity}
                        min={1}
                        max={getMaxQuantity()}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 1;
                          setQuantity(Math.max(1, Math.min(val, getMaxQuantity())));
                        }}
                        readOnly
                      />
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={() => setQuantity(Math.min(getMaxQuantity(), quantity + 1))}
                        disabled={quantity >= getMaxQuantity()}
                        aria-label="Increase quantity"
                      >
                        <i className="fas fa-plus"></i>
                      </button>
                    </div>
                    <small className="quantity-hint">Max: {getMaxQuantity()} available</small>
                  </div>
                )}

                {/* Add to Cart Button */}
                <div className="action-buttons">
                  {((addon.has_variants && selectedVariant?.in_stock) || (!addon.has_variants && addon.in_stock)) ? (
                    <Button
                      variant="primary"
                      size="lg"
                      className="add-to-cart-btn"
                      onClick={handleAddToCart}
                      disabled={addingToCart}
                    >
                      {addingToCart ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Adding to Cart...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-shopping-cart me-2"></i>
                          Add to Cart
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button variant="secondary" size="lg" className="add-to-cart-btn" disabled>
                      <i className="fas fa-ban me-2"></i>
                      Out of Stock
                    </Button>
                  )}

                  {/* Back to Event Link */}
                  {addon.event && (
                    <Link
                      to={`/events/${addon.event.slug}`}
                      className="btn btn-outline-primary back-btn"
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to Event
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Description Section */}
        {addon.description && (
          <Row className="mt-5">
            <Col md={12}>
              <div className="addon-description-card">
                <div className="description-header">
                  <h3 className="description-title">
                    <i className="fas fa-info-circle me-2"></i>
                    About This Add-on
                  </h3>
                </div>
                <div
                  className="addon-description-content"
                  dangerouslySetInnerHTML={{ __html: addon.description }}
                />
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default AddonDetailPage;

